/**
 * CrossLinkPanel — sidebar panel showing linked cards and boards for the active page.
 * Allows adding and deleting cross-links, and navigating to linked entities.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as pagesApi from '../../api/pages-api';
import type { CrossLink } from '../../api/pages-api';
import { Button } from '../../components/ui/button';

interface CrossLinkPanelProps {
  readonly pageId: string;
}

export function CrossLinkPanel({ pageId }: CrossLinkPanelProps): React.ReactElement {
  const navigate = useNavigate();
  const [crossLinks, setCrossLinks] = useState<CrossLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLinkType, setNewLinkType] = useState<'card' | 'board'>('card');
  const [newLinkId, setNewLinkId] = useState('');
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [adding, setAdding] = useState(false);

  // Fetch cross-links on mount/pageId change
  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const response = await pagesApi.listCrossLinks(pageId);
        if (!cancelled) {
          setCrossLinks(response.data ?? []);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load cross-links');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [pageId]);

  const handleAddLink = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLinkId.trim() || !newLinkTitle.trim()) return;

    setAdding(true);
    try {
      const link = await pagesApi.createCrossLink(pageId, {
        entityType: newLinkType,
        entityId: newLinkId.trim(),
        entityTitle: newLinkTitle.trim(),
      });
      setCrossLinks((prev) => [...prev, link]);
      setNewLinkId('');
      setNewLinkTitle('');
      setShowAddForm(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add link');
    } finally {
      setAdding(false);
    }
  }, [pageId, newLinkType, newLinkId, newLinkTitle]);

  const handleDeleteLink = useCallback(async (linkId: string) => {
    try {
      await pagesApi.deleteCrossLink(pageId, linkId);
      setCrossLinks((prev) => prev.filter((l) => l.entityId !== linkId));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete link');
    }
  }, [pageId]);

  const handleNavigate = useCallback((link: CrossLink) => {
    if (link.entityType === 'board') {
      navigate(`/boards/${link.entityId}`);
    } else {
      // Navigate to card detail — assumes cards are at /boards/:boardId/cards/:cardId
      // For now, use a generic card URL pattern
      navigate(`/cards/${link.entityId}`);
    }
  }, [navigate]);

  if (loading) {
    return (
      <div className="p-4" role="status" aria-live="polite">
        <p className="text-sm text-muted-foreground">Loading links...</p>
      </div>
    );
  }

  return (
    <section aria-label="Cross links" className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Linked Items</h3>
        <Button
          size="sm"
          variant="ghost"
          className="text-xs h-6 px-2"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Add link'}
        </Button>
      </div>

      {error && (
        <p className="text-xs text-destructive" role="alert">{error}</p>
      )}

      {/* Add link form */}
      {showAddForm && (
        <form onSubmit={handleAddLink} className="space-y-2 p-2 border rounded bg-muted/30">
          <div className="flex gap-2">
            <label htmlFor="cross-link-type" className="sr-only">Link type</label>
            <select
              id="cross-link-type"
              value={newLinkType}
              onChange={(e) => setNewLinkType(e.target.value as 'card' | 'board')}
              className="px-2 py-1 text-xs border rounded bg-background"
            >
              <option value="card">Card</option>
              <option value="board">Board</option>
            </select>
          </div>
          <div>
            <label htmlFor="cross-link-id" className="sr-only">Entity ID</label>
            <input
              id="cross-link-id"
              type="text"
              value={newLinkId}
              onChange={(e) => setNewLinkId(e.target.value)}
              placeholder="Entity ID"
              className="w-full px-2 py-1 text-xs border rounded bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label htmlFor="cross-link-title" className="sr-only">Title</label>
            <input
              id="cross-link-title"
              type="text"
              value={newLinkTitle}
              onChange={(e) => setNewLinkTitle(e.target.value)}
              placeholder="Title"
              className="w-full px-2 py-1 text-xs border rounded bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <Button type="submit" size="sm" variant="default" className="w-full text-xs" disabled={adding}>
            {adding ? 'Adding...' : 'Add Link'}
          </Button>
        </form>
      )}

      {/* Cross-links list */}
      {crossLinks.length === 0 && !showAddForm && (
        <p className="text-xs text-muted-foreground">No linked items.</p>
      )}

      <ul className="space-y-1" role="list" aria-label="Cross-linked items">
        {crossLinks.map((link) => (
          <li
            key={`${link.entityType}-${link.entityId}`}
            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent/50 group"
          >
            <span className="text-xs px-1.5 py-0.5 rounded bg-muted font-mono uppercase">
              {link.entityType === 'board' ? 'B' : 'C'}
            </span>
            <button
              onClick={() => handleNavigate(link)}
              className="flex-1 text-left text-xs truncate text-foreground hover:underline"
            >
              {link.entityTitle}
            </button>
            <Button
              size="sm"
              variant="ghost"
              className="text-xs h-5 px-1 opacity-0 group-hover:opacity-100 text-destructive"
              onClick={() => handleDeleteLink(link.entityId)}
              aria-label={`Remove link to ${link.entityTitle}`}
            >
              ✕
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
